import React, { FC, useMemo, useRef } from "react";
import styled from "styled-components";
import { rem, vwDesktop, vwMobile } from "../../helpers/styles";
import { BREAKPOINTS, MEDIA_DESKTOP } from "../../helpers/theme";
import SectionLines from "../SectionLines";
import ScrollContainer from "react-indiana-drag-scroll";

const SCROLL_STEP_DESKTOP = 0.9;
const SCROLL_STEP_MOBILE = 0.85;

export type ProductCard = {
  id: string;

  imageUrl: string;
  imageAlt: string;

  name: string;
  description: string;

  nutritionLines?: string[]; // up to 3 lines

  primaryCtaLabel: string;
  primaryCtaUrl: string;
  disclaimer?: string;

  secondaryCtaLabel?: string;
  secondaryCtaUrl?: string;
};

export type ProductCardsSliderSectionProps = {
  headline: string;
  descriptionHtml: string;
  primaryButtonLabel: string;
  primaryButtonUrl: string;
  secondaryButtonLabel: string;
  secondaryButtonUrl: string;
  products: ProductCard[];

  className?: string;
};

const ProductCardsSliderSection: FC<ProductCardsSliderSectionProps> = ({
  headline,
  descriptionHtml,
  primaryButtonLabel,
  primaryButtonUrl,
  secondaryButtonLabel,
  secondaryButtonUrl,
  products,
  className,
}: ProductCardsSliderSectionProps) => {
  const sectionTitleIdRef = useRef(
    `products-slider-title-${Math.random().toString(36).slice(2, 9)}`,
  );

  const sectionTitleId = sectionTitleIdRef.current;

  const items = useMemo(() => products.slice(0, 6), [products]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const scrollByViewport = (dir: "prev" | "next") => {
    const el = scrollerRef.current;
    if (!el) return;

    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia(`(max-width: ${BREAKPOINTS.DESKTOP - 1}px)`).matches;

    const factor = isMobile ? SCROLL_STEP_MOBILE : SCROLL_STEP_DESKTOP;

    const delta =
      Math.round(el.clientWidth * factor) * (dir === "next" ? 1 : -1);
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  const onCarouselKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollByViewport("prev");
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollByViewport("next");
    }
    if (e.key === "Home") {
      e.preventDefault();
      scrollerRef.current?.scrollTo({ left: 0, behavior: "smooth" });
    }
    if (e.key === "End") {
      e.preventDefault();
      scrollerRef.current?.scrollTo({
        left: scrollerRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  };

  return (
    <Section className={className} aria-labelledby={sectionTitleId}>
      <Inner>
        <Top>
          <Copy>
            <Headline id={sectionTitleId}>{headline}</Headline>
            <Description
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />

            <ButtonsRowDesktop>
              <PrimaryButton href={primaryButtonUrl}>
                {primaryButtonLabel}
              </PrimaryButton>
              <SecondaryButton href={secondaryButtonUrl}>
                {secondaryButtonLabel}
              </SecondaryButton>
            </ButtonsRowDesktop>
          </Copy>
        </Top>

        <CarouselWrap
          role="region"
          aria-roledescription="carousel"
          aria-label="Featured products"
          onKeyDown={onCarouselKeyDown}
          tabIndex={0}
          ref={scrollerRef}
        >
          <ScrollContainer
            vertical={false}
            className="scroll-container"
          >
            {items.map((p, i) => (
              <Card
                key={p.id}
                role="group"
                aria-roledescription="slide"
                aria-label={`${p.name} (${i + 1} of ${items.length})`}
              >
                <CardImageWrap>
                  <CardImage src={p.imageUrl} alt={p.imageAlt} loading="lazy" />
                </CardImageWrap>

                <CardBody>
                  <CardTop>
                    <CardTitle>{p.name}</CardTitle>
                    <CardDesc
                      dangerouslySetInnerHTML={{ __html: p.description }}
                    />

                    <Separator aria-hidden="true" />

                    <NutritionList aria-label="Nutritional information">
                      {(p.nutritionLines ?? []).slice(0, 3).map((line, idx) => (
                        <NutritionRow key={`${p.id}-n-${idx}`}>
                          <NutritionText>{line}</NutritionText>
                        </NutritionRow>
                      ))}
                    </NutritionList>

                    <CardFootnote>{p.disclaimer}</CardFootnote>
                  </CardTop>

                  <CardActions>
                    <CardPrimaryCta href={p.primaryCtaUrl}>
                      {p.primaryCtaLabel}
                    </CardPrimaryCta>
                    <CardSecondaryCta
                      href={p.secondaryCtaUrl}
                      aria-label={`Where to buy ${p.name}`}
                    >
                      {(p.secondaryCtaLabel ?? "WHERE TO BUY").toUpperCase()}
                    </CardSecondaryCta>
                  </CardActions>
                </CardBody>
              </Card>
            ))}
          </ScrollContainer>
        </CarouselWrap>

        <ButtonsRowMobile>
          <PrimaryButton href={primaryButtonUrl}>
            {primaryButtonLabel}
          </PrimaryButton>
          <SecondaryButton href={secondaryButtonUrl}>
            {secondaryButtonLabel}
          </SecondaryButton>
        </ButtonsRowMobile>
      </Inner>
    </Section>
  );
};

export default ProductCardsSliderSection;

const Section = styled.section`
  background: #012604;
  position: relative;
`;

const Inner = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: ${vwDesktop(1440)};
  padding-top: ${vwMobile(80)};
  padding-bottom: ${vwMobile(80)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding-top: ${vwDesktop(120)};
    padding-bottom: ${vwDesktop(120)};
  }
  ${SectionLines("#F6F1E7")}

  &::before,
  &::after {
    z-index: 0;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${vwMobile(20)};

  padding-left: ${vwMobile(40)};
  padding-right: ${vwMobile(40)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    gap: ${vwDesktop(40)};
    padding-left: ${vwDesktop(128)};
    padding-right: ${vwDesktop(0)};
  }
`;

const Copy = styled.div``;

const Headline = styled.h2`
  margin: 0;
  color: #b5dd71;
  font-family: "GT Alpina", serif;
  font-style: normal;
  font-weight: 300;
  line-height: 95%;
  letter-spacing: -2px;
  font-size: ${rem(40)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${rem(72)};
    letter-spacing: -3.6px;
  }
`;

const Description = styled.div`
  padding-top: ${vwMobile(20)};
  color: #f6f1e7;
  font-family: "Sharp Sans Display No1";
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
  font-size: ${rem(12)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding-top: ${vwDesktop(20)};
    font-size: ${rem(14)};
  }

  /* Styled bullet formatting */
  p {
    margin: 0 0 ${vwMobile(10)} 0;

    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      margin-bottom: ${vwDesktop(10)};
    }
  }

  ul {
    margin: 0;
    padding-left: ${vwMobile(18)};
    list-style: none;

    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      padding-left: ${vwDesktop(18)};
    }
  }

  li {
    position: relative;
    margin: ${vwMobile(6)} 0;

    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      margin: ${vwDesktop(6)} 0;
    }

    &::before {
      content: "•";
      position: absolute;
      left: -${vwMobile(14)};
      top: 0;
      color: #f6f1e7;
      opacity: 0.9;

      @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
        left: -${vwDesktop(14)};
      }
    }
  }
`;

const ButtonsRowDesktop = styled.div`
  display: none;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: flex;
    gap: ${vwDesktop(12)};
    margin-top: ${vwDesktop(40)};
    align-items: center;
  }
`;

const ButtonsRowMobile = styled.div`
  display: flex;
  gap: ${vwMobile(8)};
  padding-left: ${vwMobile(32)};
  padding-right: ${vwMobile(32)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
  }
`;

const PrimaryButton = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;

  border-radius: ${rem(431)};
  background: #b5dd71;
  color: #012604;

  font-family: "GT Alpina", serif;
  font-weight: 300;
  letter-spacing: -0.6px;
  font-size: ${rem(12)};
  line-height: normal;
  height: ${rem(35)};

  padding: 0;
  flex: 1 0 0;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    flex: 0 0 auto;
    height: auto;
    font-size: ${rem(16)};
    line-height: 100%;
    letter-spacing: -0.64px;
    padding: ${vwDesktop(16)} ${vwDesktop(32)};
  }
  &:hover {
    background: #f6f1e7;
    color: #012604;
  }
`;

const SecondaryButton = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;

  border-radius: ${rem(431)};
  border: ${rem(0.501)} solid #f6f1e7;
  color: #f6f1e7;

  font-family: "GT Alpina", serif;
  font-weight: 300;
  letter-spacing: -0.6px;
  font-size: ${rem(12)};
  line-height: normal;

  height: ${rem(35)};
  padding: 0;
  flex: 1 0 0;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    flex: 0 0 auto;
    height: auto;
    font-size: ${rem(16)};
    line-height: 100%;
    letter-spacing: -0.64px;
    padding: ${vwDesktop(16)} ${vwDesktop(32)};
  }

  &:hover {
    background: #f6f1e7;
    color: #012604;
  }
`;

const CarouselWrap = styled.div`
  display: flex;
  width: 100%;
  margin-top: ${vwMobile(40)};
  padding-bottom: ${vwMobile(40)};

  & .scroll-container {
    display: flex;
    overflow-x: scroll;
    gap: ${vwMobile(20)};
    padding-inline: ${vwMobile(40)};

    ${MEDIA_DESKTOP} {
      padding-inline: ${vwDesktop(128)};
      gap: ${vwDesktop(20)};
    }
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-top: ${vwDesktop(60)};
    padding-bottom: ${vwDesktop(0)};
  }

  &:focus-visible {
    outline: none;
  }
`;

const Card = styled.article`
  flex: 0 0 auto;
  width: ${vwMobile(258)};
  background: #f6f1e7;
  border-radius: ${rem(12)};
  overflow: hidden;
  scroll-snap-align: start;

  display: flex;
  flex-direction: column;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${vwDesktop(317.5)};
  }

  &:focus-within {
  }
`;

const CardImageWrap = styled.div`
  display: flex;
  height: ${rem(170)};
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    height: ${rem(229)};
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  -webkit-user-drag: none;
  user-select: none;
`;

const CardBody = styled.div`
  display: flex;
  padding: ${vwMobile(20)} ${vwMobile(20)};
  flex-direction: column;

  flex: 1;
  align-items: flex-start;
  min-height: ${rem(258)};
  gap: ${rem(18)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: ${vwDesktop(24)} ${vwDesktop(20)};
    min-height: ${rem(305)};
  }
`;

const CardTop = styled.div`
  width: 100%;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #012604;
  font-family: "GT Alpina", serif;
  font-size: ${rem(32)};
  font-style: normal;
  font-weight: 300;
  line-height: 90%;
  letter-spacing: -1.6px;
`;

const CardDesc = styled.div`
  margin: ${vwMobile(20)} 0 0 0;
  color: #012604;
  font-family: "Sharp Sans Display No1";
  font-size: ${rem(12)};
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
  padding-bottom: ${vwMobile(25)};
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${rem(14)};
    margin: ${vwDesktop(20)} 0 0 0;
    padding-bottom: ${vwDesktop(25)};
  }
`;

const Separator = styled.div`
  height: 1px;
  width: 100%;
  background: rgba(1, 38, 4, 0.18);
`;

const NutritionList = styled.div`
  display: flex;
  flex-direction: column;
`;

const NutritionRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${rem(8)};
  padding-bottom: ${rem(5)};
  padding-top: ${rem(5)};
  border-bottom: 1px solid rgba(1, 38, 4, 0.14);
`;

const NutritionText = styled.span`
  color: #012604;
  font-family: "GT Pressura Mono";
  font-size: ${rem(13)};
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
`;

const CardFootnote = styled.p`
  margin: ${rem(13)} 0 0 0;
  color: #012604;
  font-family: "Sharp Sans Display No1";
  font-size: ${rem(12)};
  font-style: normal;
  font-weight: 500;
  line-height: 130%;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin: ${rem(15)} 0 0 0;
  }
`;

const CardActions = styled.div`
  width: 100%;
  display: flex;
  gap: ${rem(10)};
  align-items: center;

  margin-top: auto;
`;

const CardPrimaryCta = styled.a`
  display: flex;
  padding: ${vwMobile(5)} ${vwMobile(15)};
  justify-content: center;
  align-items: center;
  text-decoration: none;

  border-radius: ${rem(431)};
  background: #b5dd71;
  color: #012604;

  font-family: "GT Pressura Mono";
  font-size: ${rem(10)};
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
  text-transform: uppercase;
  white-space: nowrap;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: ${vwDesktop(8)} ${vwDesktop(16)};
    font-size: ${rem(12)};
  }
  &:hover {
    background: #012604;
    color: #b5dd71;
  }
`;

const CardSecondaryCta = styled(CardPrimaryCta)`
  border: ${rem(0.501)} solid #012604;
  background: transparent;
  color: #012604;

  &:hover {
    color: #f6f1e7;
  }
`;
